const BRIEF = String.raw`\bbriefs?\b`;
const HERE = String.raw`here(?!\s+are\b|'s\b)`;
const POINTER = String.raw`\b(?:${HERE}|this link|the link|link below|following link)\b`;
const CLICK = String.raw`\b(?:click|tap)\b`;
const VERB = String.raw`\b(?:view|see|read|download|open|access|find)\b`;
const DESTINATION = String.raw`(?:\bhere(?!\s+are\b|'s\b)\b|\b(?:this|the|following) link\b|\blink below\b|\b(?:in|via|at)\s+(?:the\s+|this\s+|a\s+)?(?:document|pdf|docx?|google\s+doc|drive|file|attachment)\b|https?:|\battached\b)`;

function documentUrlPattern(): RegExp {
  return /(?:https?:\/\/|www\.)[^\s<>"']+/gi;
}

const FILLER_WORD =
  /^(?:here|click|tap|brief|briefs|link|the|a|an|to|for|view|see|this|at|is|available|download|open|read|google|doc|docs|document|pdf|full|complete|actual|of|and|your|our)$/i;

/**
 * True when overview copy tells creators the brief lives at a link or in another file,
 * instead of in the overview itself.
 */
export function briefOverviewRedirectsExternally(html: string): boolean {
  const text = htmlToPlainText(html);
  if (!text) {
    return false;
  }
  if (isMostlyDocumentUrl(text)) {
    return true;
  }
  return splitSentences(text).some((sentence) => sentenceRedirects(sentence));
}

function htmlToPlainText(html: string): string {
  const withHrefs = html.replace(
    /<a\b[^>]*\bhref\s*=\s*["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi,
    (_match, href: string, label: string) => `${label} ${href}`,
  );
  const withBreaks = withHrefs
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(?:p|div|li|h[1-6]|tr)>/gi, '\n');
  return decodeEntities(withBreaks.replace(/<[^>]+>/g, ' '))
    .replace(/[^\S\n]+/g, ' ')
    .replace(/[ \t]*\n[ \t]*/g, '\n')
    .trim();
}

function decodeEntities(value: string): string {
  return value
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&quot;/gi, '"');
}

function splitSentences(text: string): string[] {
  return text
    .split(/[\n.!?]+/)
    .map((sentence) => sentence.trim())
    .filter((sentence) => sentence.length > 0);
}

function sentenceRedirects(sentence: string): boolean {
  const hasBrief = new RegExp(BRIEF, 'i').test(sentence);
  const clickThenPointer = new RegExp(`${CLICK}[\\s\\S]{0,80}${POINTER}`, 'i');
  const pointerThenClick = new RegExp(`${POINTER}[\\s\\S]{0,40}${CLICK}`, 'i');
  if (hasBrief && (clickThenPointer.test(sentence) || pointerThenClick.test(sentence))) {
    return true;
  }

  const verbBriefDestination = new RegExp(`${VERB}[\\s\\S]{0,40}${BRIEF}[\\s\\S]{0,60}${DESTINATION}`, 'i');
  const verbPointerBrief = new RegExp(`${VERB}[\\s\\S]{0,40}${POINTER}[\\s\\S]{0,40}${BRIEF}`, 'i');
  if (verbBriefDestination.test(sentence) || verbPointerBrief.test(sentence)) {
    return true;
  }

  const briefLivesElsewhere = new RegExp(
    `${BRIEF}[\\s\\S]{0,60}\\b(?:is here|are here|is available|available at|can be found|is at|lives at|see attached|in the attached|in this document|in the pdf|in the google doc|in the doc|in the file|in the drive|in this pdf|in this file)\\b`,
    'i',
  );
  if (briefLivesElsewhere.test(sentence)) {
    return true;
  }

  return hasBrief && sentenceHasDocumentUrl(sentence);
}

function sentenceHasDocumentUrl(sentence: string): boolean {
  return documentUrls(sentence).length > 0;
}

function isMostlyDocumentUrl(text: string): boolean {
  if (documentUrls(text).length === 0) {
    return false;
  }
  const withoutUrls = text.replace(documentUrlPattern(), ' ');
  const words = withoutUrls.split(/[^\p{L}\p{N}]+/u).filter((word) => word.length > 0);
  return words.every((word) => FILLER_WORD.test(word));
}

function documentUrls(text: string): string[] {
  return [...text.matchAll(documentUrlPattern())]
    .map((match) => match[0])
    .filter((url) => isDocumentUrl(url));
}

function isDocumentUrl(url: string): boolean {
  return /docs\.google\.com|drive\.google\.com|dropbox\.com|notion\.(?:so|site)|\.pdf(?:[?#]|$)/i.test(url);
}
